import { FC } from 'react';
import Typography, { type TypographyProps } from 'src/common/components/Typography';
import { SHARED_MEDIA_QUERIES as sharedMediaQueries } from 'src/common/constants';
import { User } from '../../types/api/v4/auth/user';
import getClassNames from '../../utils/getClassNames';
import Img, { type ImgProps } from '../Img';

import styles from './Avatar.module.scss';

export type UserAvatar = Pick<User, 'has_photo' | 'first_name' | 'last_name' | 'photo_url'>;

const colors = [
  'blue',
  'purple',
  'green',
  'red',
  'yellow',
];

export const avatarSizes = ['xs', 'small', 'medium', 'large', 'extraLarge'] as const;
type AvatarSize = typeof avatarSizes[number];

export type AvatarProps = {
  classNameModifier?: string;
  size?: AvatarSize;
  user: UserAvatar;
};

type Config = {
  typography: Record<NonNullable<AvatarProps['size']>, TypographyProps['size']>;
  /** These size values exist here in js and in css
   * When the ImgOptimzed component with `fill` prop is available, these js values can be deleted.
   * Because the Avatar will fill to the sizes defined in the css
  */
  img: Record<NonNullable<AvatarProps['size']>, Pick<ImgProps, 'width' | 'height'>>;
};

const config: Config = {
  typography: {
    xs: 'caption',
    small: 'body-l',
    medium: 'heading-4',
    large: 'heading-2',
    extraLarge: 'heading-1',
  },
  img: {
    xs: {
      width: 24,
      height: 24,
    },
    small: {
      width: 40,
      height: 40,
    },
    medium: {
      width: 64,
      height: 64,
    },
    large: {
      width: 90,
      height: 90,
    },
    extraLarge: {
      width: 120,
      height: 120,
    },
  },
};

const Avatar: FC<AvatarProps> = ({
  classNameModifier,
  size = 'small',
  user: {
    has_photo: hasPhoto,
    first_name: firstName,
    last_name: lastName,
    photo_url: photoUrl,
  },
}) => {
  // To keep the avatar colours the same,
  // the alphabet is split into fifths with each colour assigned to a fifth
  const alphabetIndex = firstName && firstName.toUpperCase().charCodeAt(0);
  const color = colors[alphabetIndex % 5];
  const modifiers = `${classNameModifier} ${color} ${size}`;

  const { height, width } = config.img[size];

  return (
    <div className={getClassNames(styles, 'root', modifiers)}>
      {hasPhoto && photoUrl ? (
        <Img
          src={photoUrl}
          alt={firstName ?? 'User image'}
          height={height}
          width={width}
          srcSetParams={[{
            w: width, h: height, query: sharedMediaQueries.xs,
          }]}
          imageParams={{
            fit: 'crop',
            w: width,
            h: height,
          }}
        />
      ) : (
        <div className={styles.placeholder}>
          <Typography
            size={config.typography[size]}
            weight="regular"
            type="p"
          >
            {firstName && firstName.charAt(0)}
            {lastName && lastName.charAt(0)}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default Avatar;
